import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { FileTypeOptions } from 'v2/utils/drmFileUtils';
import styles from './BulkFilesUpload.module.scss';
import React, { useEffect, useRef } from 'react';
import Checkbox from 'v2/common/CheckBox/Checkbox';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'translations';
import { DataVendor } from 'v2/models';
import { DropzoneFileItem } from 'v2/common/DropzoneFileItem';
import { useFilesDropzone } from 'v2/common/FilesDropzone/hooks';
import FilesDropzone from 'v2/common/FilesDropzone/FilesDropzone';
import { DataVendorSelector } from 'v2/common/DataVendorSelector';
import { CompanyFileAssociation } from '../CompanyFileAssociation';
import Dropzone, { DropzoneFile } from 'dropzone';
import { BulkFilesUploadForm } from 'v2/common/BulkImport/BulkImportModal/types';
import LoadingSpinner from 'v2/common/LoadingSpinner/LoadingSpinner';

const selectStyles = {
  control: {
    width: '100%',
    borderColor: 'var(--light-gray)',
  },
  dropdownIndicator: {
    right: '10px',
    justifyContent: 'flex-end',
  },
};

export function BulkFilesUpload() {
  const { t } = useTranslation();
  const { control, setValue, register, setError, trigger } =
    useFormContext<BulkFilesUploadForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bulkUploadFiles',
  });
  const bulkUploadFiles = useWatch({ control, name: 'bulkUploadFiles' });
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...bulkUploadFiles?.[index],
    };
  });

  const dropzoneRef = useRef<HTMLDivElement>(null);
  const { dropzone, files, maxAllowedBulkUploadedFiles, isLoading } =
    useFilesDropzone({
      dropzoneRef,
      onFileAdded(file) {
        append({
          file,
          isSelected: false,
          dataVendor: null,
        });
      },
      onFileRemoved(file) {
        const fieldIndexToRemove = controlledFields.findIndex(
          (field) => field.file === file
        );

        if (fieldIndexToRemove !== -1) remove(fieldIndexToRemove);
      },
    });

  const shouldRenderCompanyFileSelection = files.length > 0;
  const selectedFiles = controlledFields.filter((field) => field.isSelected);
  const hasSelectedAllTheFiles =
    selectedFiles.length === controlledFields.length;

  const allSelectedFilesHasSameCompany =
    selectedFiles.length > 0 &&
    selectedFiles.every(
      (file) =>
        file.dataVendor &&
        selectedFiles[0].dataVendor &&
        file.dataVendor.id === selectedFiles[0].dataVendor.id
    );

  useEffect(() => {
    if (files.length === 0) return;

    const successfulFiles = fields
      .map((field) => field.file)
      .filter((file) => file.status === Dropzone.SUCCESS);

    const errorFiles = fields
      .map((field) => field.file)
      .filter((file) => file.status === Dropzone.ERROR);

    const pendingFiles = fields
      .map((field) => field.file)
      .filter((file) => file.status === Dropzone.UPLOADING);

    const fileUploadIDs = successfulFiles
      .map((file) => file.fileId)
      .filter((fileUploadId): fileUploadId is string => Boolean(fileUploadId));

    if (successfulFiles.length === files.length) {
      setError('fileUploadIDs', {
        type: 'validate',
        message: undefined,
      });
      setValue('fileUploadIDs', JSON.stringify(fileUploadIDs));
    } else if (pendingFiles.length > 0) {
      setError('fileUploadIDs', {
        type: 'validate',
        message: 'There are files being uploaded.',
      });
      setValue('fileUploadIDs', '');
    } else if (errorFiles.length > 0) {
      setError('fileUploadIDs', {
        type: 'validate',
        message: 'There are files with errors.',
      });
      setValue('fileUploadIDs', '');
    }
  }, [files]);

  const onSelectAllChangeHandler = () => {
    controlledFields.forEach((_field, index) => {
      setValue(`bulkUploadFiles.${index}.isSelected`, !hasSelectedAllTheFiles);
    });
    trigger();
  };

  const onUpdataAllFieldsWithDataVendor = (dataVendor?: DataVendor) => {
    controlledFields.forEach((field, index) => {
      if (field.isSelected)
        setValue(`bulkUploadFiles.${index}.dataVendor`, dataVendor ?? null, {
          shouldValidate: true,
        });
    });
  };

  const onFileRemoved = (file: DropzoneFile) => {
    dropzone?.removeFile(file);
    trigger();
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <div>
        <FormLabel label={`File Type`} required={false} />

        <Controller
          name="type"
          control={control}
          render={({ field: { onChange } }) => {
            return (
              <GeneralSelect
                aria-label="File type dropdown"
                options={FileTypeOptions}
                handleChange={(option) => onChange(option?.value)}
                customStyles={selectStyles}
              />
            );
          }}
        />
      </div>

      <div>
        <FormLabel label={`Your files (${files.length})`} required={false} />

        {shouldRenderCompanyFileSelection && (
          <p className={styles.hintText}>
            Use the dropdowns below to designate which{' '}
            {t(TranslationKeys.company_one)} each file is associated with.
          </p>
        )}

        <div
          hidden={shouldRenderCompanyFileSelection}
          className={styles.bulkFilesDropzone}
        >
          <input
            type="hidden"
            aria-label="File upload IDs"
            {...register?.('fileUploadIDs', {
              required: true,
            })}
          />
          <FilesDropzone
            ref={dropzoneRef}
            maxAllowedBulkUploadedFiles={maxAllowedBulkUploadedFiles}
          />

          {dropzone && (
            <div className={styles.files}>
              {files.map((file, index) => (
                <DropzoneFileItem
                  key={index}
                  file={file}
                  onDelete={(file) => onFileRemoved(file)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {shouldRenderCompanyFileSelection && (
        <div>
          <div className={styles.selectAllCompanyHeader}>
            <Checkbox
              label={'Select All'}
              checked={hasSelectedAllTheFiles}
              onChange={onSelectAllChangeHandler}
            />

            {selectedFiles.length > 0 && (
              <div className={styles.dataVendorSelector}>
                <DataVendorSelector
                  onChange={onUpdataAllFieldsWithDataVendor}
                  value={
                    allSelectedFilesHasSameCompany
                      ? selectedFiles[0].dataVendor
                      : null
                  }
                />
              </div>
            )}
          </div>

          <div className={styles.files}>
            {dropzone &&
              controlledFields.map((field, index) => (
                <Controller
                  key={field.id}
                  control={control}
                  name={`bulkUploadFiles.${index}`}
                  render={({ field: { onChange } }) => (
                    <CompanyFileAssociation
                      dataVendor={field.dataVendor}
                      file={field.file}
                      isFileSelected={field.isSelected}
                      onFileDelete={(file) => onFileRemoved(file)}
                      onFileCheckboxChange={() =>
                        onChange({
                          ...field,
                          isSelected: !field.isSelected,
                        })
                      }
                      onDataVendorChange={(dataVendor) =>
                        onChange({
                          ...field,
                          dataVendor,
                        })
                      }
                    />
                  )}
                />
              ))}
          </div>
        </div>
      )}
    </>
  );
}
